import { PlusOutlined } from "@ant-design/icons";
import {
  Badge,
  Button,
  Card,
  Divider,
  Drawer,
  Flex,
  Input,
  Popconfirm,
  Select,
  Space,
  Typography,
} from "antd";
import Title from "antd/es/typography/Title";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import api from "../../../../api";
import Loader from "../../../../components/Loader";
import { IntTplVehicleDocumentType } from "../../../../domain/types";
import { useAppSelector } from "../../../../store";
import { AppState } from "../../../../store/appSlice";
import AdminLangSwicher from "../../../admin_components/AdminLangSwicher";
const Text = Typography;

const InternationalTplVehicleDocumentType = () => {
  const { t } = useTranslation();
  const { field_Language } = useAppSelector(
    (data: { app: AppState }) => data.app,
  );
  const [drawerStatus, setDrawerStatus] = useState<boolean>(false);
  const [editedState, setEditedState] =
    useState<Partial<IntTplVehicleDocumentType> | null>(null);

  const {
    data: IntTplVehicleDodumentTypesList,
    refetch: refetchIntTplVehicleDocumentTypes,
    isLoading: isIntTplVehicleDocumentTypesLoading,
  } = api.useGetIntTplVehicleDocumentypesQuery(undefined);

  const {
    data: intTplVehicleDocumentTypeById,
    isLoading: isIntTplVehicleDocumentTypeByIdLoading,
  } = api.useGetIntTplVehicleDocumentTypeByIdQuery(editedState?.id as number, {
    skip: !editedState?.id,
  });

  const [
    createVehicleDocumentType,
    { isLoading: isCreateIntTplVehicleDocumentTypeLoading },
  ] = api.useCreateIntTplVehicleDocumentTypeMutation();

  const [
    updateVehicleDocumentType,
    { isLoading: isUpdateIntTplVehicleDocumentTypeLoading },
  ] = api.useUpdateIntTplVehicleDocumentTypeMutation();

  const onCloseDrawer = () => {
    setDrawerStatus(false);
  };

  const setEditedField = (
    field: keyof IntTplVehicleDocumentType,
    value: any,
  ) => {
    setEditedState({
      productId: 1,
      ...editedState,
      [field]: value,
    });
  };

  const [
    deleteVehicleDocumentType,
    {
      isLoading: isDeleteIntTplVehicleDocumentTypeLoading,
      isSuccess: deleteIntTplVehicleDocumentTypeSuccess,
    },
  ] = api.useDeleteIntTplVehicleDocumentTypeMutation();

  const _createClientDocumentType = async () => {
    if (editedState) {
      try {
        await createVehicleDocumentType(editedState).unwrap();
        refetchIntTplVehicleDocumentTypes();
        toast.success(`${t("carDocumentType")} ${t("addedSuccessfully")}`);
        onCloseDrawer();
      } catch (error) {
        if (error?.data?.errors?.constructor === Array) {
          const firstError = error.data.errors[0];
          const errorMessage = firstError.message;
          toast.error(errorMessage);
        } else {
          toast.error(`${t("errorOccurred")}`);
        }
      }
    } else {
      toast.error(`${t("fillAllFields")}`);
    }
  };

  const _updateClientDocumentType = async () => {
    if (editedState) {
      try {
        await updateVehicleDocumentType(editedState).unwrap();
        refetchIntTplVehicleDocumentTypes();
        toast.success(`${t("carDocumentType")} ${t("updatedSuccessfully")}`);
        onCloseDrawer();
      } catch (error) {
        if (error?.data?.errors?.constructor === Array) {
          const firstError = error.data.errors[0];
          const errorMessage = firstError.message;
          toast.error(errorMessage);
        } else {
          toast.error(`${t("errorOccurred")}`);
        }
      }
    } else {
      toast.error(`${t("fillAllFields")}`);
    }
  };

  useEffect(() => {
    if (deleteIntTplVehicleDocumentTypeSuccess) {
      refetchIntTplVehicleDocumentTypes();
    }
  }, [
    deleteIntTplVehicleDocumentTypeSuccess,
    refetchIntTplVehicleDocumentTypes,
  ]);

  useEffect(() => {
    if (intTplVehicleDocumentTypeById) {
      setEditedState(intTplVehicleDocumentTypeById);
      setDrawerStatus(true);
    }

    return () => {};
  }, [intTplVehicleDocumentTypeById]);

  const MainLoader = isIntTplVehicleDocumentTypesLoading;

  const DrawerLoader =
    isUpdateIntTplVehicleDocumentTypeLoading ||
    isDeleteIntTplVehicleDocumentTypeLoading ||
    isIntTplVehicleDocumentTypeByIdLoading ||
    isCreateIntTplVehicleDocumentTypeLoading;

  return (
    <>
      {MainLoader ? (
        <Loader />
      ) : (
        <>
          <Flex justify="space-between">
            <Button
              size="large"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => [setEditedField("id", 0), setDrawerStatus(true)]}
            >
              {t("create")}
            </Button>

            <AdminLangSwicher />
          </Flex>

          <Divider />

          <Flex gap={20} wrap="wrap" justify="space-evenly">
            {IntTplVehicleDodumentTypesList?.map((item, index) => (
              <Badge.Ribbon
                key={index}
                style={{
                  marginTop: "-20px",
                }}
                text={item.status === "active" ? t("active") : t("inActive")}
                color={item.status === "active" ? "green" : "red"}
              >
                <Card
                  hoverable
                  style={{
                    maxWidth: "320px",
                    width: "100vw",
                    display: "grid",
                    placeItems: "center",
                    height: "160px",
                  }}
                  onClick={() => [
                    setEditedField("id", item.id),
                    setDrawerStatus(!drawerStatus),
                  ]}
                >
                  <Title level={5}>
                    {item.translations?.[field_Language]?.name ||
                      t("textNotFound")}
                  </Title>

                  <Text>
                    {t("docNumberOfPages")}: <b>{item?.neededSides}</b>
                  </Text>
                </Card>
              </Badge.Ribbon>
            ))}
          </Flex>

          <Drawer
            title={editedState?.id === 0 ? t("create") : t("update")}
            onClose={onCloseDrawer}
            open={drawerStatus}
            extra={<AdminLangSwicher />}
          >
            {DrawerLoader ? (
              <Loader />
            ) : (
              <Flex vertical gap={20}>
                <Space direction="vertical">
                  <Text>{t("carDocumentType")}</Text>
                  <Input
                    size="large"
                    placeholder={t("carDocumentType")}
                    value={
                      editedState?.translations?.[field_Language]?.name || ""
                    }
                    onChange={(event) =>
                      setEditedField("translations", {
                        ...editedState?.translations,
                        [field_Language]: {
                          ...editedState?.translations?.[field_Language],
                          name: event?.target?.value,
                        },
                      })
                    }
                  />
                </Space>

                <Space direction="vertical">
                  <Text>{t("docNumberOfPages")}</Text>
                  <Input
                    type="number"
                    size="large"
                    placeholder={t("docNumberOfPages")}
                    value={editedState?.neededSides || ""}
                    onChange={(event) =>
                      setEditedField("neededSides", event.target.value)
                    }
                  />
                </Space>

                <Space direction="vertical">
                  <Text>{t("status")}</Text>
                  <Select
                    size="large"
                    showSearch
                    style={{ width: "100%", display: "block" }}
                    placeholder={t("status")}
                    optionFilterProp="label"
                    allowClear
                    value={editedState?.status || undefined}
                    onChange={(value) => setEditedField("status", value)}
                    options={[
                      {
                        status: "active",
                        name: t("active"),
                      },
                      {
                        status: "inactive",
                        name: t("inActive"),
                      },
                    ]?.map((item) => ({
                      value: item.status,
                      label: item.name,
                    }))}
                  />
                </Space>

                <>
                  <Divider style={{ margin: 0 }} />
                  {editedState?.id === 0 ? (
                    <Button
                      disabled={
                        editedState && editedState.status ? false : true
                      }
                      size="large"
                      type="primary"
                      onClick={_createClientDocumentType}
                    >
                      {t("create")}
                    </Button>
                  ) : null}

                  {editedState && editedState.id && editedState.id !== 0 ? (
                    <>
                      <Button
                        size="large"
                        type="primary"
                        onClick={_updateClientDocumentType}
                      >
                        {t("update")}
                      </Button>

                      <Popconfirm
                        title={t("certainToDeleteIt")}
                        onConfirm={() => [
                          deleteVehicleDocumentType(editedState?.id as number),
                          onCloseDrawer(),
                        ]}
                        onCancel={() => {}}
                        okText="Yes"
                        cancelText="No"
                        placement="bottom"
                      >
                        <Button
                          danger
                          size="large"
                          style={{
                            width: "100%",
                          }}
                        >
                          {t("delete")}
                        </Button>
                      </Popconfirm>
                    </>
                  ) : null}
                </>
              </Flex>
            )}
          </Drawer>
        </>
      )}
    </>
  );
};

export default InternationalTplVehicleDocumentType;
